import React, { useEffect, useMemo, useState } from "react"
import { createContext, ReactNode, useContext } from "react"
import { BaseRetorikConfig, RetorikConfiguration } from "../configs/retorikConfig"
import useTimer from "../hooks/useTimer"
import { Disposition } from "../models/dispositionEnum"
import { SessionState } from "../models/sessionState"
import { UserInfo } from "../models/userInfo"

type Props = {
    children: ReactNode
}

type AmnContextType = {
    userInfo : UserInfo,
    randomizeQuestions : boolean,
    retorikConfig : RetorikConfiguration,
    environmentChoice: boolean,
    showEnvironmentChoiceButton: boolean,
    disposition : Disposition,
    pdfToDisplay : string | undefined,
    showPDF : {show : boolean, disposition : string},
    showPDFButtons : boolean,
    showPDFQRCode : boolean,
    showBot : boolean,
    retreiveConversation : boolean,
    testMode : boolean,
    sessionState : SessionState,
    conversationId:string,
    timer : { 
        retreive : (erase? : boolean) => void
        get : (name: string) => void
        start : (name: string) => void
        pause : (name: string) => void
        resume : (name: string) => void
        reset : (name: string) => void
        isRunning : {[name: string]: boolean}
        timers : {[name: string]: number}
    }
    setRetorikConfig: (x: RetorikConfiguration) => void
    setRandomizeQuestions: (x: boolean) => void
    setDisposition: (x: Disposition) => void
    setEnvironmentChoice: (x: boolean) => void
    setShowEnvironmentChoiceButton: (x: boolean) => void
    setPdfToDisplay : (x : string) => void
    setShowPDF : ({show, disposition}) => void
    setShowPDFButtons : (x : boolean) => void
    setShowPDFQRCode : (x : boolean) => void
    setUserInfo : (x : UserInfo) => void
    setShowBot : (x : boolean) => void
    setRetreiveConversation : (x : boolean) => void
    setTestMode : (x : boolean) => void
    setSessionState: (x : SessionState) => void
    setConversationId:(x:string)=> void
}

const AmnContextDefaultValues: AmnContextType = {
    userInfo : {gender : "male", voice : "male"},
    randomizeQuestions : false,
    retorikConfig : BaseRetorikConfig,
    disposition: Disposition.Enseignant,
    environmentChoice: false,
    showEnvironmentChoiceButton: false,
    pdfToDisplay : undefined,
    showPDF : {show : false, disposition: "full"},
    showPDFButtons : false,
    showPDFQRCode : false,
    showBot : false,
    retreiveConversation : false,
    testMode : false,
    sessionState : SessionState.NoSession,
    conversationId:'',
    timer:{
        retreive : () => {},
        get : () => {},
        start : () => {},
        pause : () => {},
        resume : () => {},
        reset : () => {},
        isRunning:{},
        timers:{}
    },
    setRetorikConfig: () => {},
    setRandomizeQuestions: () => {},
    setDisposition: () => {},
    setEnvironmentChoice: () => {},
    setShowEnvironmentChoiceButton: () => {},
    setPdfToDisplay : () => {},
    setShowPDF : () => {},
    setShowPDFButtons: () => {},
    setShowPDFQRCode: () => {},
    setUserInfo: () => {},
    setShowBot: () => {},
    setRetreiveConversation : () => {},
    setTestMode : () => {},
    setSessionState: () => {},
    setConversationId: () => {},
}

export const AmnContext = createContext<AmnContextType>( AmnContextDefaultValues );

export function useAmn() {
    return useContext(AmnContext)
}

export function AmnProvider({ children }: Props) {
    const [retorikConfig, setRetorikConfig] = useState<RetorikConfiguration>(AmnContextDefaultValues.retorikConfig)
    const [randomizeQuestions, setRandomizeQuestions] = useState<boolean>(false)
    const [disposition, setDisposition] = useState<Disposition>(Disposition.Enseignant)
    const [environmentChoice, setEnvironmentChoice] = useState<boolean>(false)
    const [showEnvironmentChoiceButton, setShowEnvironmentChoiceButton] = useState<boolean>(false)
    const [pdfToDisplay, setPdfToDisplay] = useState<string|undefined>()
    const [showPDF, setShowPDF] = useState<{show:boolean, disposition : string}>({show:false, disposition : "full"})
    const [showPDFButtons, setShowPDFButtons] = useState<boolean>(false)
    const [showPDFQRCode, setShowPDFQRCode] = useState<boolean>(false)
    const [retreiveConversation, setRetreiveConversation] = useState<boolean>(false)
    const [showBot, setShowBot] = useState<boolean>(false)
    const [userInfo, setUserInfo] = useState<UserInfo>({gender : "male", voice: "male"})
    const [testMode, setTestMode] = useState<boolean>(false)
    const [sessionState, setSessionState] = useState<SessionState>(SessionState.NoSession)
    const [conversationId, setConversationId] = useState<string>('')
    

    const {
        start,
        pause,
        resume,
        reset,
        get,
        isRunning,
        timer,
        retreive
      } = useTimer();
    
    const value = useMemo(()=> ({
            userInfo: userInfo,
            retorikConfig : retorikConfig,
            randomizeQuestions : randomizeQuestions,
            disposition : disposition,
            environmentChoice: environmentChoice,
            showEnvironmentChoiceButton: showEnvironmentChoiceButton,
            timer : {
                start : start,
                pause : pause,
                resume : resume,
                get : get,
                reset : reset,
                retreive : retreive,
                isRunning : isRunning,
                timers : timer
            },
            pdfToDisplay: pdfToDisplay,
            showPDF: showPDF,
            showPDFButtons : showPDFButtons,
            showPDFQRCode : showPDFQRCode,
            showBot : showBot,
            retreiveConversation : retreiveConversation,
            testMode: testMode,
            sessionState,
            conversationId:conversationId,
            setRetorikConfig,
            setRandomizeQuestions,
            setDisposition,
            setEnvironmentChoice,
            setShowEnvironmentChoiceButton,
            setPdfToDisplay,
            setShowPDF,
            setShowPDFButtons,
            setShowPDFQRCode,
            setUserInfo,
            setShowBot,
            setRetreiveConversation,
            setTestMode,
            setSessionState,
            setConversationId
        }),[
            userInfo,
            retorikConfig, 
            randomizeQuestions,
            disposition,
            environmentChoice,
            showEnvironmentChoiceButton,
            timer,
            isRunning,
            pdfToDisplay,
            showPDF,
            showPDFButtons,
            showPDFQRCode,
            retreiveConversation,
            showBot,
            testMode,
            sessionState,
            conversationId,
            setRetorikConfig,
            setRandomizeQuestions,
            setDisposition,
            setEnvironmentChoice,
            setShowEnvironmentChoiceButton,
            get,
            start,
            pause,
            reset,
            resume,
            retreive,
            setPdfToDisplay,
            setShowPDF,
            setShowPDFButtons,
            setShowPDFQRCode,
            setUserInfo,
            setRetreiveConversation,
            setShowBot,
            setTestMode,
            setSessionState,
            setConversationId
        ]);
    
    useEffect(()=>{
        console.log("retorikConfig", retorikConfig);
    }, [retorikConfig])

    useEffect(()=>{
        console.log("timers", timer);
    }, [timer])

    
    useEffect(()=>{
        console.log("randomizeQuestions changed : ", randomizeQuestions);
        localStorage.setItem("randomizeQuestions", JSON.stringify(randomizeQuestions));
    }, [randomizeQuestions])

    useEffect(()=>{
        localStorage.setItem("disposition", JSON.stringify(disposition));
    }, [disposition])

    return (
        <React.Fragment>
            <AmnContext.Provider value={value}>
                {children}
            </AmnContext.Provider>
        </React.Fragment>
        )
    }